import { useReducer } from 'react'

import type {
  CRMObject,
  DraftEmailProperties,
  DraftSlackProperties,
} from 'types/graphql'

import { dayjs } from './dayjs'
import { NativeObjectTypes } from './objects'

export type SuggestedContextState = {
  parentObject: CRMObject
  workspaceId: string
  referencedObjects: CRMObject[]
  dismissedObjects: CRMObject[]
  suggestedObjects: CRMObject[]
}

export const cleanObjects = (objects: CRMObject[]) => {
  return objects
    .map((o) => ({
      objectId: o.objectId,
      objectType: o.objectType,
      workspaceId: o.workspaceId,
    }))
    .filter((o) => o.objectId && o.objectType)
}

export type SuggestedContextAction =
  | {
      type: 'CONTEXT_OBJECTS_ADDED'
      payload: CRMObject[]
    }
  | {
      type: 'SUGGESTED_CONTEXT_RETURNED'
      payload: {
        objectsToAdd: CRMObject[]
        objectsToSuggest: CRMObject[]
      }
    }
  | {
      type: 'CONTEXT_OBJECT_DISMISSED'
      payload: CRMObject
    }

const suggestedContextReducer = (
  state: SuggestedContextState,
  action: SuggestedContextAction
): SuggestedContextState => {
  switch (action.type) {
    case 'CONTEXT_OBJECTS_ADDED': {
      const newReferencedObjects = [
        ...state.referencedObjects,
        ...action.payload.filter(
          (newObj) =>
            !state.referencedObjects.some(
              (existingObj) => existingObj.objectId === newObj.objectId
            )
        ),
      ]

      return {
        ...state,
        parentObject: newReferencedObjects?.[0],
        suggestedObjects: state.suggestedObjects.filter(
          (existingObject) =>
            !action.payload.some(
              (newObject) => newObject.objectId === existingObject.objectId
            )
        ),
        referencedObjects: newReferencedObjects,
      }
    }
    case 'CONTEXT_OBJECT_DISMISSED': {
      const newReferencedObjects = state.referencedObjects.filter(
        (o) => o.objectId !== action.payload.objectId
      )

      const newDismissedObjects = [...state.dismissedObjects, action.payload]

      return {
        ...state,
        parentObject: newReferencedObjects?.[0],
        referencedObjects: newReferencedObjects,
        suggestedObjects: state.suggestedObjects.filter(
          (o) => o.objectId !== action.payload.objectId
        ),
        dismissedObjects: newDismissedObjects,
      }
    }
    case 'SUGGESTED_CONTEXT_RETURNED': {
      const combinedObjects = [
        ...state.referencedObjects,
        ...action.payload.objectsToAdd,
      ]

      const uniqueReferencedObjectsMap = new Map<string, CRMObject>()
      combinedObjects.forEach((obj) => {
        if (obj.objectId && !uniqueReferencedObjectsMap.has(obj.objectId)) {
          uniqueReferencedObjectsMap.set(obj.objectId, obj)
        }
      })

      const uniqueReferencedObjects = Array.from(
        uniqueReferencedObjectsMap.values()
      )

      return {
        ...state,
        suggestedObjects: cleanObjects(action.payload.objectsToSuggest).filter(
          (suggestedObj) =>
            !uniqueReferencedObjects.some(
              (refObj) => refObj.objectId === suggestedObj.objectId
            )
        ),
        parentObject: action.payload.objectsToAdd?.[0] ?? state.parentObject,
        referencedObjects: cleanObjects(uniqueReferencedObjects),
      }
    }
  }
  return state
}

const initializeState = ({
  parentObject,
  initialContextObjects,
  initialDismissedObjects,
  workspaceId,
}: {
  parentObject: Partial<CRMObject>
  initialContextObjects: Partial<CRMObject>[]
  initialDismissedObjects: Partial<CRMObject>[]
  workspaceId: string
}) => {
  return {
    parentObject: {
      ...parentObject,
      workspaceId,
    },
    referencedObjects: initialContextObjects.map((o) => ({
      ...o,
      workspaceId,
    })),
    dismissedObjects: initialDismissedObjects.map((o) => ({
      ...o,
      workspaceId,
    })),
    suggestedObjects: [],
  } as SuggestedContextState
}

export const useSuggestedContextReducer = ({
  parentObject,
  initialContextObjects,
  initialDismissedObjects,
  workspaceId,
}: {
  parentObject: Partial<CRMObject>
  initialContextObjects: Partial<CRMObject>[]
  initialDismissedObjects: Partial<CRMObject>[]
  workspaceId: string
}): { state: SuggestedContextState; dispatch } => {
  const [state, dispatch] = useReducer(
    suggestedContextReducer,
    {
      parentObject,
      initialContextObjects,
      initialDismissedObjects,
      workspaceId,
    },
    initializeState
  )

  return { state, dispatch }
}

export const systemPromptForParentObject = ({
  parentObject,
  userCoreContact,
}: {
  parentObject: DocumentObject
  userCoreContact: any
}) => {
  if (parentObject?.objectType === NativeObjectTypes.Page) {
    return systemPromptForPage()
  } else if (parentObject?.objectType === NativeObjectTypes.Draft) {
    return systemPromptForDraft({
      parentObject,
      userCoreContact,
    })
  }
  return systemPromptForPage()
}

const systemPromptForPage = () => {
  return `You are an the world's best personal assistant, brilliantly proficient in effective, direct business communication, focused always on the details that matter in interpersonal relationships.
  You are an expert in using CRM data to connect the dots for your user, and diligently follow their instructions.
  Use only h3 and smaller headings. NEVER return an empty html element with only whitespace or no content.
  Be sure to use the custom <span> tags provided in the context when referencing specific objects, like people and organizations and other objects.
  Do not write or include page titles for your responses.
  Return the raw inner HTML with just your response, no other text, introductory or otherwise, only string that contains one or more HTML tags.
  Do not include line breaks in your response.`
}

// this is a page-like object that can be used as a parent object of a page
export type DocumentObject =
  | {
      objectType: 'native_page'
      objectId: string
      workspaceId: string
    }
  | {
      objectType: 'native_thread'
      objectId: string
      workspaceId: string
    }
  | {
      objectType: 'native_draft'
      objectId: string
      workspaceId: string
      properties:
        | {
            channel: 'EMAIL'
            email: DraftEmailProperties
          }
        | {
            channel: 'SLACK'
            slack: DraftSlackProperties
          }
    }

const systemPromptForDraft = ({
  parentObject,
  userCoreContact,
}: {
  parentObject: DocumentObject
  userCoreContact: any
}) => {
  if (parentObject.objectType !== 'native_draft') {
    throw new Error('Parent object is not a draft')
  }

  const draft = parentObject.properties

  if (draft.channel === 'EMAIL') {
    // Construct the user details string
    const userDetails = `Name: ${userCoreContact.firstName} ${userCoreContact.lastName}\nEmail: ${userCoreContact.email}`

    // Construct the email draft details string
    let emailDraftDetails = ''
    if (draft.email.subject) {
      emailDraftDetails += `Subject: ${draft.email.subject}\n`
    }
    if (draft.email.to?.length) {
      emailDraftDetails += `To: ${draft.email.to.join(', ')}\n`
    }
    if (draft.email.cc?.length) {
      emailDraftDetails += `Cc: ${draft.email.cc.join(', ')}\n`
    }
    if (draft.email.bcc?.length) {
      emailDraftDetails += `Bcc: ${draft.email.bcc.join(', ')}\n`
    }
    // Remove trailing newline if present
    emailDraftDetails = emailDraftDetails.trim()

    return `You are an AI assistant specialized in writing emails. Your task is to compose an email based on the provided information and guidelines.

First, review the details of the user you're writing on behalf of:

<user_details>
${userDetails}
</user_details>

The current date as of your writing is ${dayjs().format('MMM D, YYYY')}.
"Yesterday" is ${dayjs().subtract(1, 'day').format('MMM D, YYYY')}.
"Tomorrow" is ${dayjs().add(1, 'day').format('MMM D, YYYY')}.
If you are following up on a meeting or email, be careful to pay attention to the date of the meeting or email.
If it was not a recent meeting or email, do not mention the date as if it just happened.

Now, consider the specific details for the email you need to draft:

<email_draft_details>
${emailDraftDetails || 'No specific details provided for this draft.'}
</email_draft_details>

Before writing the email, please plan its structure and content. Write your email plan inside <email_strategy> tags.

<email_strategy>
1. Identify the recipient and determine the appropriate greeting.
2. Analyze the recipient's relationship to the sender and adjust tone accordingly.
3. List the main points to be covered in the email body.
4. Brainstorm potential subject lines (even though you won't include one in the final email).
5. Consider potential obstacles or objections the recipient might have and plan how to address them.
6. Decide on an appropriate sign-off based on the context.
7. Consider the tone and style appropriate for this email (casual, friendly, but professional).
8. Plan how to structure the content, including any lists if necessary.
9. Double-check that your plan adheres to the formatting rules, especially regarding lists and paragraph spacing.
10. Newlines: Do not include any double newline characters (\\n\\n) in your response.
</email_strategy>

Now, write the email based on your planning. Follow these important guidelines:

1. Format: Use HTML tags to structure the email. Enclose paragraphs in <div> tags.

2. Spacing:
   - Use an empty <div></div> for visual separation between paragraphs or distinct content blocks.
   - Exception: Do NOT place an empty <div></div> immediately before or after a numbered list (<ol>) or a bulleted list (<ul>).

3. Lists: Use <ol> for numbered lists and <ul> for bulleted lists.

4. Text Formatting: Use <i> for italic, <b> for bold, and <u> for underlined text.

5. Raw Output Format: Your entire response must be a single, continuous string of HTML. Do not include literal newline characters (like \\n) in the raw output string. All visual line breaks within the email content itself MUST be created using appropriate HTML tags (e.g., <div>, <br>).

6. Greeting and Sign-off:
   - If you know the recipient's name, address them by their first name.
   - Sign the email as "${userCoreContact.firstName}" unless there's a compelling reason to do otherwise.
   - Use a casual sign-off like "Best" (not "Best regards").
   - Include an empty <div></div> between the main body and the sign-off, unless the body ends with a list.

7. Style and Tone:
   - Be concise, action-oriented, and use straightforward language.
   - Keep the tone friendly and appreciative, but slightly informal.
   - Avoid unnecessary adjectives or large words.

8. Perspective: Write as if you are the person sending the email (from the perspective of ${userCoreContact.email}).

9. Content: Do not add any extra sentences or words beyond what's necessary to convey the message effectively.

10. Subject Line: Do NOT include a subject line in your response.

Your response should contain only the HTML-formatted email body, without any additional text or explanations. Here's an example of the desired output format:

<div>Hi [Recipient's First Name],</div>
<div></div>
<div>I hope this email finds you well. I'm writing to [main purpose of the email].</div>
<div></div>
<div>Here are the key points:</div>
<ul>
<li>Point 1</li>
<li>Point 2</li>
<li>Point 3</li>
</ul>
<div>Let me know if you need any further information.</div>
<div></div>
<div>Best,</div>
<div>[Your First Name]</div>

Please proceed with writing the email based on the provided information and guidelines.`
  } else if (parentObject.properties.channel === 'SLACK') {
    return `<system>
      You are an expert in writing Slack messages.
      </system>`
  }
}
